import { useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { Sparkles } from "lucide-react";

async function downloadStream(path, payload, filename) {
  const r = await fetch(API + path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  if (!r.ok) { toast.error("Export failed"); return; }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Exports() {
  const [busy, setBusy] = useState(false);
  const [aiText, setAiText] = useState("");

  const execPDF = async () => { setBusy(true); try { await downloadStream("/exports/executive-pdf", {}, "apocalynx-executive.pdf"); toast.success("PDF downloaded"); } finally { setBusy(false); } };

  const boardPPTX = async () => {
    setBusy(true);
    try {
      const ex = await api.post("/ai/board-summary");
      setAiText(ex.data.text);
      const m = ex.data.metrics?.counters || {};
      const s = ex.data.metrics?.scores || {};
      await downloadStream("/exports/pptx", {
        title: "APOCALYNX Board Pack",
        slides: [
          { title: "Operational Posture", bullets: [`Operational ${s.operational}/100`, `Compliance ${s.compliance}/100`, `Security ${s.security}/100`, `Asset ${s.asset}/100`, `Business ${s.business}/100`],
            kpis: [{label:"Clients", value: m.clients}, {label:"Assets", value: m.total_assets}, {label:"Open Tickets", value: m.open_tickets}, {label:"Recurring", value: m.recurring_issues}] },
          { title: "AI Board Summary", bullets: (ex.data.text || "").split(/\n\n|\n- |\n\* /).slice(0, 8).map(s => s.trim()).filter(Boolean) },
          { title: "Revenue Leakage", bullets: [`Monthly waste: $${ex.data.leakage?.monthly_waste_usd?.toLocaleString()}`, `Quarterly: $${ex.data.leakage?.quarterly_waste_usd?.toLocaleString()}`, `Annual: $${ex.data.leakage?.annual_waste_usd?.toLocaleString()}`] },
        ],
      }, "apocalynx-board-pack.pptx");
      toast.success("Board pack downloaded");
    } finally { setBusy(false); }
  };

  const dashboardXLSX = async () => {
    setBusy(true);
    try {
      const { data: exec } = await api.get("/dashboard/executive");
      const { data: rec } = await api.get("/recurring-issues");
      await downloadStream("/exports/excel", {
        sheets: [
          { name: "Executive", headers: ["Metric", "Value"], rows: [...Object.entries(exec.scores), ...Object.entries(exec.counters)] },
          { name: "Recurring", headers: ["Category", "Asset", "Occurrences", "Severity", "Estimated Cost USD"], rows: rec.map(r => [r.category, r.asset_id, r.occurrences, r.severity, r.estimated_cost_usd]) },
        ],
      }, "apocalynx-dashboard.xlsx");
      toast.success("Workbook downloaded");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">APOCALYNX Export Engine</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Executive-Grade Reports</h1>
        <p className="text-sm text-zinc-500 mt-1">Real PDF · Excel · PowerPoint generation, fully APOCALYNX-branded.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ExportCard title="Executive PDF" subtitle="One-click branded PDF with health scores and compliance frameworks" cta="Generate PDF" testid="export-pdf" onClick={execPDF} busy={busy}/>
        <ExportCard title="Board Pack (PPTX)" subtitle="AI-driven board pack with KPI strip + narrative" cta="Generate PPTX" testid="export-pptx" onClick={boardPPTX} busy={busy} ai/>
        <ExportCard title="Workbook (XLSX)" subtitle="Executive + recurring-issue analytics" cta="Generate XLSX" testid="export-xlsx" onClick={dashboardXLSX} busy={busy}/>
      </div>
      {aiText && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="font-display font-medium mb-2">AI Board Narrative (embedded into PPTX)</div>
          <pre className="text-xs whitespace-pre-wrap text-zinc-300 leading-relaxed" data-testid="board-ai-text">{aiText}</pre>
        </div>
      )}
    </div>
  );
}

function ExportCard({ title, subtitle, cta, onClick, busy, ai, testid }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
      <div className="flex items-center gap-2"><FileDown size={14} className="text-blue-400"/><div className="font-display font-medium">{title}</div>{ai && <Sparkles size={12} className="text-amber-400"/>}</div>
      <div className="text-sm text-zinc-400 mt-2">{subtitle}</div>
      <button onClick={onClick} disabled={busy} data-testid={testid} className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white text-sm rounded px-3 py-2">{busy ? "Working…" : cta}</button>
    </div>
  );
}
